// user-form.schema.ts
import {AbstractControl, AsyncValidatorFn, FormGroup, ValidationErrors, ValidatorFn} from '@angular/forms';
import {UserStore} from '@data-users';
import {catchError, debounceTime, first, map, Observable, of, switchMap} from 'rxjs';
import {PasswordRequirement} from '../../../models/user-form.types';
import {DefaultCommunicationSrcType} from '@core/api/data-access';

export class UserFormValidator {
	static readonly passwordRequirements: PasswordRequirement[] = [
		{key: 'length', text: 'dccomp.user-form.passwordMinLength', isValid: false, check: (p: string) => p.length >= 8},
		{key: 'uppercase', text: 'dccomp.user-form.passwordUppercase', isValid: false, check: (p: string) => /[A-Z]/.test(p)},
		{key: 'lowercase', text: 'dccomp.user-form.passwordLowercase', isValid: false, check: (p: string) => /[a-z]/.test(p)},
		{key: 'number', text: 'dccomp.user-form.passwordNumber', isValid: false, check: (p: string) => /\d/.test(p)},
		{key: 'special', text: 'dccomp.user-form.passwordSpecialChar', isValid: false, check: (p: string) => /[@$!%*?&^#()_+=\-[\]{};':"\\|,.<>/~`]/.test(p)},

		// Passwords Match
		{key: 'match', text: 'dccomp.user-form.passwordsMustMatch', isValid: false, check: (p: string, cp: string) => p && cp && p === cp},
	];

	public static getPasswordRequirementsCopy(): PasswordRequirement[] {
		return [...UserFormValidator.passwordRequirements];
	}

	static passwordsMatch: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null; // Should be applied to a FormGroup
		}

		const password = control.get('password')?.value;
		const confirmPassword = control.get('confirmPassword')?.value;

		// Only return error if both have values and they don't match.
		// The 'required' validator handles empty fields.
		if (password && confirmPassword && password !== confirmPassword) {
			// Set error on the FormGroup level
			return {passwordsDoNotMatch: true};
		}

		return null; // Passwords match or one/both are empty/not yet filled
	};

	// New validator for radio group
	static communicationPreferencesRadioValidator(phoneControl: AbstractControl, emailControl: AbstractControl): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			if (!(control instanceof AbstractControl)) {
				return null; // Should be applied to a FormGroup
			}
			const checkRadioValue: DefaultCommunicationSrcType = control.value;

			if (!checkRadioValue || checkRadioValue === DefaultCommunicationSrcType.None) {
				return null; // No need to check if the radio is not selected
			}

			const phone = phoneControl.value;
			const email = emailControl.value;

			// prevent check 'email' if email is empty
			// prevent check 'phonenumber' or 'whatsapp' if phone is empty
			if (checkRadioValue === DefaultCommunicationSrcType.notifyEmail && !email) {
				return {
					cannotCheckEmailIfEmailIsEmpty: true,
				};
			}
			if ((checkRadioValue === DefaultCommunicationSrcType.notifySMS || checkRadioValue === DefaultCommunicationSrcType.notifyWhatsapp) && !phone) {
				return {
					cannotCheckPhoneIfPhoneIsEmpty: true,
				};
			}

			return null;
		};
	}

	static requireAtLeastOneCheckbox: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null; // Should be applied to a FormGroup
		}

		let checked = 0;
		Object.keys(control.controls).forEach(key => {
			const checkboxControl = control.get(key);
			if (checkboxControl?.value === true) {
				checked++;
			}
		});

		if (checked === 0) {
			return {requireAtLeastOneCheckbox: true}; // Error key
		}

		return null; // At least one checkbox is checked
	};

	static phoneInternationalRegex = new RegExp('^\\+[1-9][\\d,-]{1,14}$');
	static phoneLocalRegex = new RegExp('^([0]\\d{1,3}[-])?\\d{7,10}$');

	static phoneNumberValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const phoneNumber = control.value;
		if (!phoneNumber) {
			return null; // אם אין ערך, לא נחשב שגיאה
		}
		if (phoneNumber.match(UserFormValidator.phoneInternationalRegex) || phoneNumber.match(UserFormValidator.phoneLocalRegex)) {
			return null;
		}
		return {invalidPhoneNumber: true};
	};

	// Only English letters and numbers are allowed (no special characters, no spaces)
	static readonly usernamePattern = /^[a-zA-Z0-9]+$|^[^@\s]+@[^@\s]+\.[^@\s]+$|^([0]\d{1,3}[-])?\d{7,10}$/;
	static isValidUsername(username: string): boolean {
		return UserFormValidator.usernamePattern.test(username);
	}
	static usernameValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		const username = control.value;
		if (!username || UserFormValidator.isValidUsername(username)) return null;
		return {invalidUsername: true};
	};

	/**
	 * @description Check if (username,	email, or phone) already exists in the system
	 * @param userStore
	 * @param identifier - 'username' | 'email' | 'phonenumber'
	 * @param debounce - Optional parameter to specify debounce time in milliseconds
	 * @param allowedValues - Optional array of allowed values to ignore in the check (e.g., in edit mode - we can ignore the current value)
	 * @errors
	 * - null (if not exists identifier)
	 * - usernameExists: true
	 * - emailExists: true
	 * - phonenumberExists: true
	 */
	static userIdentifierExists(userStore: UserStore, identifier: 'username' | 'email' | 'phonenumber', debounce = 300, allowedValues: string[] = []): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			const identifierValue = control.value;
			if (!identifierValue) {
				return of(null);
			}
			if (identifierValue && allowedValues.includes(identifierValue)) {
				return of(null); // Ignore the check if the value is in the allowed values
			}

			return of(identifierValue).pipe(
				debounceTime(debounce), // Add debounce time to avoid too many requests
				switchMap(identifierValue =>
					userStore.isUserIdentifierPropertyExist(identifierValue).pipe(
						map(isTaken => (isTaken ? {[`${identifier}Exists`]: true} : null)), // Map to error object if taken
						catchError(() => of(null)), // Return null if there's an error
					),
				),
				first(), // Complete the observable after the first emission
			);
		};
	}

	static phoneOrEmailRequired: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
		if (!(control instanceof FormGroup)) {
			return null;
		}

		const email = control.get('email')?.value;
		const phone = control.get('phonenumber')?.value;

		if ((!email || email.trim() === '') && (!phone || phone.trim() === '')) {
			return {phoneOrEmailRequired: true};
		}

		return null;
	};
}
